import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, Clock, TrendingUp } from 'lucide-react';
import { useSearch, useDebounce } from '../../hooks';
import styles from './SearchModal.module.css';

export default function SearchModal({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const { results, loading, search, clearSearch } = useSearch();
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Focus en el input cuando se abre
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Buscar cuando cambia el query
  useEffect(() => {
    if (debouncedQuery) {
      search(debouncedQuery);
    } else {
      clearSearch();
    }
  }, [debouncedQuery]);

  // Cerrar con Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/buscar?q=${encodeURIComponent(query)}`);
      onClose();
    }
  };

  const handleResultClick = () => {
    setQuery('');
    clearSearch();
    onClose();
  };

  if (!isOpen) return null;

  const recentSearches = ['Economía', 'Deportes', 'Política', 'Tecnología'];
  const trendingTopics = ['Elecciones 2024', 'Cambio climático', 'Fútbol', 'Innovación'];

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>
          <X size={24} />
        </button>

        <form onSubmit={handleSubmit} className={styles.searchForm}>
          <Search size={24} className={styles.searchIcon} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar noticias, temas, autores..."
            className={styles.searchInput}
          />
          {query && (
            <button 
              type="button" 
              className={styles.clearButton}
              onClick={() => setQuery('')}
            >
              <X size={18} />
            </button>
          )}
        </form>

        <div className={styles.content}>
          {loading && (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <span>Buscando...</span>
            </div>
          )}

          {!loading && query && results.articles.length > 0 && (
            <div className={styles.results}>
              <h3 className={styles.resultsTitle}>Artículos</h3>
              <ul className={styles.resultsList}>
                {results.articles.slice(0, 5).map(article => (
                  <li key={article.id}>
                    <Link 
                      to={`/articulo/${article.slug}`}
                      className={styles.resultItem}
                      onClick={handleResultClick}
                    >
                      <div className={styles.resultContent}>
                        <span className={styles.resultCategory}>
                          {article.category?.name || 'General'}
                        </span>
                        <h4 className={styles.resultTitle}>{article.title}</h4>
                        <p className={styles.resultExcerpt}>{article.excerpt}</p>
                      </div>
                      <ArrowRight size={18} className={styles.resultArrow} />
                    </Link>
                  </li>
                ))}
              </ul>
              {results.articles.length > 5 && (
                <Link 
                  to={`/buscar?q=${encodeURIComponent(query)}`}
                  className={styles.viewAll}
                  onClick={handleResultClick}
                >
                  Ver todos los resultados ({results.articles.length})
                </Link>
              )}
            </div>
          )}

          {!loading && query && results.articles.length === 0 && (
            <div className={styles.noResults}>
              <p>No se encontraron resultados para "{query}"</p>
              <span>Intenta con otros términos de búsqueda</span>
            </div>
          )}

          {!query && (
            <div className={styles.suggestions}>
              <div className={styles.suggestionSection}>
                <h3 className={styles.suggestionTitle}>
                  <Clock size={16} />
                  Búsquedas recientes
                </h3>
                <ul className={styles.suggestionList}>
                  {recentSearches.map((term, index) => (
                    <li key={index}>
                      <button 
                        className={styles.suggestionItem}
                        onClick={() => setQuery(term)}
                      >
                        {term}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className={styles.suggestionSection}>
                <h3 className={styles.suggestionTitle}>
                  <TrendingUp size={16} />
                  Tendencias
                </h3>
                <ul className={styles.suggestionList}>
                  {trendingTopics.map((term, index) => (
                    <li key={index}>
                      <button 
                        className={styles.suggestionItem}
                        onClick={() => setQuery(term)}
                      >
                        {term}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <span>Presiona <kbd>ESC</kbd> para cerrar</span>
        </div>
      </div>
    </div>
  );
}
